import clsx from "clsx";
import React, { Dispatch, FC, SetStateAction, useEffect, useMemo } from "react";
import { useFormContext } from "react-hook-form";

import { MaterialType } from "../../../../types/materials";
import { TechSheetFieldType } from "../../../../types/products";
import { getTechSheetFieldsListFromSpecializations } from "../../../../utils/products";
import { Button } from "../../../buttons/button";
import { Select } from "../../../forms";
import { MaterialsFormFields } from "../../../forms/forms-fields/materials-form-fields";
import {
  MaterialOption,
  MaterialsFormFieldsLabels,
} from "../../../forms/forms-fields/materials-form-fields/types";
import { Icon } from "../../../icon";
import { EMPTY_OPTION_VALUE } from "../constants";
import { ProductFinderFiltersFormTypes } from "../types";
import { MultiSelectFieldFilter, SingleSelectFieldFilter, TextFieldFilter } from "./fields-filters";

export interface ProductFiltersPanelProps {
  specializations: Queries.MachineSpecializationDataFragment[];
  labels: {
    "product-finder-panel-machine-type": string;
    "product-finder-panel-clear-filters": string;
    "product-finder-panel-apply-filters": string;
    "product-finder-panel-option-all": string;
    "product-finder-panel-no-filter-found": string;
  } & MaterialsFormFieldsLabels;
  isOpen: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
}

export const ProductFiltersPanel: FC<ProductFiltersPanelProps> = ({
  labels,
  specializations,
  isOpen,
  setOpen,
}) => {
  const isSingleSpecialization = specializations.length === 1;
  const form = useFormContext<ProductFinderFiltersFormTypes>();

  const [materials, machiningTypeId] = form.watch(["materials", "machiningTypeId"]);
  const materialsCount = Object.values(materials).filter(Boolean).length;

  // Resets machiningTypeId to "all" (empty value) if selected value no longer matches materials
  const { watch, setValue } = form;
  useEffect(() => {
    if (specializations.length === 1) return;
    const machiningTypeId = watch("machiningTypeId");
    const machiningType = specializations
      .flatMap((s) => s.machiningType)
      .find((mt) => mt?.id === machiningTypeId);
    if (
      materialsCount === 0 ||
      !machiningType || // should never happen (this is just for TypeScript)
      !materials[machiningType.material as MaterialOption]
    )
      setValue("machiningTypeId", EMPTY_OPTION_VALUE);
  }, [materials, materialsCount, specializations, watch, setValue]);

  const specializationsForSelectedMaterials = useMemo<
    Queries.MachineSpecializationDataFragment[]
  >(() => {
    if (materialsCount === 0) return [];
    return specializations.filter((s) => {
      const material = s.machiningType?.material;
      const materialMatch = material && materials[material as MaterialOption];
      const machiningTypeMatch = !machiningTypeId || s.machiningType?.id === machiningTypeId;
      return materialMatch && machiningTypeMatch;
    });
  }, [materials, materialsCount, specializations, machiningTypeId]);

  const availableTechSheetFields = useMemo(() => {
    return getTechSheetFieldsListFromSpecializations(specializationsForSelectedMaterials);
  }, [specializationsForSelectedMaterials]);

  return (
    <div
      className={clsx(
        "transition-all duration-300 rounded-3xl",
        "bg-neutral-light overflow-hidden w-full relative",
        isOpen ? "max-h-[200vh] overflow-y-auto" : "max-h-0"
      )}
    >
      <div className="overflow-y-auto">
        {isOpen && (
          <button className="absolute right-3 top-3" type="button" onClick={() => setOpen(false)}>
            <Icon icon="close" className="w-6 h-6" />
          </button>
        )}
        {!isSingleSpecialization && (
          <div
            className={clsx(
              "w-full h-full py-3",
              "flex flex-col md:flex-row md:items-start gap-2 lg:gap-12"
            )}
          >
            <MaterialsFormFields
              labels={labels}
              className="px-5"
              materialsToShow={Object.values(MaterialType) as MaterialOption[]}
            />
            <Select
              {...form.register("machiningTypeId")}
              className="px-6 lg:px-0 w-fit"
              disabled={materialsCount === 0}
              label={labels["product-finder-panel-machine-type"]}
            >
              <option value={EMPTY_OPTION_VALUE}>
                {labels["product-finder-panel-option-all"]}
              </option>
              {specializations.map((specialization, i) => {
                const shouldBeDisplayed =
                  specialization.machiningType?.material &&
                  materials[specialization.machiningType.material as MaterialOption];
                return specialization.machiningType ? (
                  <option
                    value={specialization.machiningType.id}
                    key={i}
                    className={clsx(!shouldBeDisplayed && "hidden")}
                  >
                    {specialization.machiningType.name}
                  </option>
                ) : null;
              })}
            </Select>
          </div>
        )}
        <div
          className={clsx(
            "rounded-3xl overflow-hidden transition-[max-height]",
            "max-h-[100vh] border-2 duration-300 bg-primary-light border-primary-light "
          )}
        >
          <div className={clsx("bg-light", "p-4 pb-4 rounded-b-3xl")}>
            <div className="flex flex-row gap-6 pb-4 flex-wrap items-start">
              {!availableTechSheetFields.length && (
                <div className="mx-auto pt-3">{labels["product-finder-panel-no-filter-found"]}</div>
              )}
              {availableTechSheetFields
                .filter((field) => field?.valueType === TechSheetFieldType.MULTI_SELECT)
                .sort((a, b) =>
                  (a?.allowedValues?.length ?? 0) < (b?.allowedValues?.length ?? 0) ? 1 : -1
                )
                .map((field) => {
                  if (!field) return null;
                  return <MultiSelectFieldFilter {...field} key={field.internalName} />;
                })}

              {availableTechSheetFields
                .filter((field) => field?.valueType === TechSheetFieldType.SINGLE_SELECT)
                .map((field) => {
                  if (!field) return null;
                  return (
                    <SingleSelectFieldFilter labels={labels} {...field} key={field?.internalName} />
                  );
                })}

              {availableTechSheetFields
                .filter((field) => field?.valueType === TechSheetFieldType.TEXT)
                .map((field) => {
                  if (!field) return null;
                  return <TextFieldFilter {...field} key={field?.internalName} />;
                })}
            </div>
            {availableTechSheetFields.length > 0 && (
              <div className="w-full flex justify-end">
                <Button variant="primary" size="sm" type="submit">
                  {labels["product-finder-panel-apply-filters"]}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
